import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';

const faqs = [
  {
    q: 'What is AINAA?',
    a: 'AINAA (Artist Identification Network & Accreditation Archive) is a professional association that maintains verified trust records for artists and creative professionals. It functions as a credibility body — not a marketplace or social network.',
  },
  {
    q: 'How does the Trust Score work?',
    a: 'The Trust Score is measured on a scale of 0–1000, based on five dimensions: Credibility, Communication, Discipline, Consistency, and Growth. It is calculated by Introwspect, our proprietary behavioral intelligence algorithm, using verified professional data — not self-reported information.',
  },
  {
    q: 'Who can become a member?',
    a: 'Membership is by verification only. You must be recommended or verified by an existing trusted member of the AINAA network. There is no open registration. Applicants join a waitlist and are reviewed individually.',
  },
  {
    q: 'What is Introwspect?',
    a: 'Introwspect is AINAA\'s proprietary behavioral intelligence algorithm. It analyses your work patterns, professional interactions, and growth trajectories to generate your Trust Score and surface insights about your strengths, blind spots, and areas for improvement.',
  },
  {
    q: 'What is Specter™?',
    a: 'Specter™ is a floating, context-aware AI assistant available inside the AINAA app. It knows your Trust Score, goals, and professional history. You can ask it about career strategy, score improvement, financial advice, or personal support. It adapts its tone to what you need.',
  },
  {
    q: 'Is AINAA free?',
    a: 'AINAA operates on a membership model. Details regarding fee structure and tiers are shared with applicants during the verification process. The waitlist application itself is free.',
  },
  {
    q: 'How is AINAA different from LinkedIn or Behance?',
    a: 'AINAA is not a portfolio site or social network. It is a credibility body. Your Trust Score is calculated from verified data, not endorsements or likes. Membership itself is a credential — it signals that you have been vetted by trusted professionals.',
  },
  {
    q: 'Can my Trust Score go down?',
    a: 'Yes. The Trust Score is dynamic and reflects your ongoing professional conduct. Late deliveries, unresolved disputes, or inactivity can negatively impact specific dimensions. Specter™ can help you identify and recover from score declines.',
  },
  {
    q: 'What data does AINAA use to calculate my score?',
    a: 'AINAA uses verified professional data: project completion records, peer reviews, communication response times, dispute history, and growth trajectories. It does not use social media activity, follower counts, or self-reported achievements.',
  },
  {
    q: 'Is my data private?',
    a: 'Yes. Your Trust Score details, behavioral insights, and personal data are visible only to you. Other members can see your overall Trust Score and verification status, but not the dimensional breakdown or underlying data.',
  },
  {
    q: 'What happens after I join the waitlist?',
    a: 'Your application is reviewed by the AINAA verification committee. If a trusted member vouches for you or your professional record meets the threshold, you will receive an invitation to complete the onboarding process.',
  },
  {
    q: 'Can I use AINAA to find clients or jobs?',
    a: 'AINAA is not a job board. However, your verified Trust Score and membership status serve as professional credentials that clients, galleries, and institutions can reference when evaluating your reliability.',
  },
  {
    q: 'What are the five dimensions of the Trust Score?',
    a: 'The five dimensions are: Credibility (track record and reputation), Communication (responsiveness and clarity), Discipline (deadline adherence and reliability), Consistency (quality maintenance over time), and Growth (professional development trajectory).',
  },
  {
    q: 'How often is the Trust Score updated?',
    a: 'The Trust Score is updated continuously as new verified data becomes available. Significant changes are reflected within 24–48 hours. You can track historical trends through your dashboard.',
  },
  {
    q: 'What kind of artists can join AINAA?',
    a: 'AINAA is open to all creative professionals — visual artists, musicians, writers, filmmakers, designers, performers, and anyone whose work involves creative output. The common requirement is verifiable professional activity.',
  },
  {
    q: 'Does AINAA offer legal or financial support?',
    a: 'Yes. AINAA members have access to a Legal Aid panel for contract disputes and unpaid commissions, as well as financial advisory resources. Specter™ can connect you to these services directly.',
  },
  {
    q: 'What is the highest Trust Score ever recorded?',
    a: 'The highest Trust Score on record is 960 out of 1000. Scores above 900 are exceptionally rare and indicate sustained excellence across all five dimensions over an extended period.',
  },
  {
    q: 'Can organisations or galleries use AINAA?',
    a: 'Institutional partnerships are available. Galleries, festivals, and arts organisations can verify the Trust Scores of artists they work with, subject to the artist\'s consent. Contact us for institutional access.',
  },
  {
    q: 'Is there an AINAA mobile app?',
    a: 'The AINAA mobile app is currently in development. Specter™ and your Trust Score dashboard will be available on mobile. Waitlist members will receive priority access upon launch.',
  },
  {
    q: 'How do I contact AINAA?',
    a: 'You can reach us through the Contact page on this website. For membership enquiries, use the waitlist application form. For institutional or press enquiries, email is preferred.',
  },
];

const FAQItem = ({ faq, index, isOpen, onToggle }) => (
  <div
    className="border-b border-[#D8D3CB] last:border-b-0"
    data-testid={`faq-item-${index}`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-5 px-1 text-left group"
      data-testid={`faq-toggle-${index}`}
    >
      <span className="text-sm sm:text-base font-semibold text-[#0F1B2D] pr-4 group-hover:text-[#C4A35A] transition-colors">
        {faq.q}
      </span>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.25 }}
        className="flex-shrink-0"
      >
        <ChevronDown size={18} className="text-[#9B9589]" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <p className="text-sm text-[#4A4640] font-medium leading-relaxed pb-5 px-1">
            {faq.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const visibleFaqs = showAll ? faqs : faqs.slice(0, 5);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section
      className="section-padding relative"
      data-testid="faq-section"
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 border-2 border-[#0F1B2D] flex items-center justify-center">
              <HelpCircle size={18} className="text-[#0F1B2D]" />
            </div>
          </div>
          <span className="section-label mb-4 block">Frequently Asked Questions</span>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0A1220] tracking-tight mt-4">
            What you need to know
          </h2>
        </div>

        <div className="govt-panel p-6 sm:p-8">
          {visibleFaqs.map((faq, i) => (
            <FAQItem
              key={i}
              faq={faq}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>

        {!showAll && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAll(true)}
              className="btn-ainaa-secondary px-8 py-3 text-sm font-semibold uppercase tracking-wider"
              data-testid="faq-show-more-btn"
            >
              Show More
            </button>
          </div>
        )}

        {showAll && (
          <div className="text-center mt-8">
            <button
              onClick={() => {
                setShowAll(false);
                setOpenIndex(null);
              }}
              className="btn-ainaa-secondary px-8 py-3 text-sm font-semibold uppercase tracking-wider"
              data-testid="faq-show-less-btn"
            >
              Show Less
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQSection;
